
export const removeUndefined = (obj: any): any => {
  if (obj === null || obj === undefined || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(removeUndefined);
  if (obj instanceof Date) return obj;

  const cleaned: any = {};
  Object.keys(obj).forEach(key => {
    const value = obj[key];
    // Remove undefined and empty strings (treat them the same)
    if (value !== undefined && value !== '') {
      cleaned[key] = removeUndefined(value);
    }
  });
  return cleaned;
};

export const deepEqual = (obj1: any, obj2: any): boolean => {

  const norm1 = removeUndefined(obj1);
  const norm2 = removeUndefined(obj2);

  if (norm1 === norm2) return true;

  if ((norm1 === null || norm1 === undefined || norm1 === '') && (norm2 === null || norm2 === undefined || norm2 === '')) return true;
  if (norm1 === null || norm2 === null || norm1 === undefined || norm2 === undefined) return norm1 === norm2;
  if (typeof norm1 !== 'object' || typeof norm2 !== 'object') return norm1 === norm2;

  if (norm1 instanceof Date && norm2 instanceof Date) {
    return norm1.getTime() === norm2.getTime();
  }
  if (norm1 instanceof Date) {
    return norm1.getTime() === (norm2 instanceof Date ? norm2.getTime() : new Date(norm2).getTime());
  }
  if (norm2 instanceof Date) {
    return (norm1 instanceof Date ? norm1.getTime() : new Date(norm1).getTime()) === norm2.getTime();
  }

  if (Array.isArray(norm1) && Array.isArray(norm2)) {
    if (norm1.length !== norm2.length) return false;
    const sorted1 = [...norm1].sort();
    const sorted2 = [...norm2].sort();
    return sorted1.every((val, idx) => deepEqual(val, sorted2[idx]));
  }

  // Handle objects
  const keys1 = Object.keys(norm1);
  const keys2 = Object.keys(norm2);
  if (keys1.length !== keys2.length) return false;

  return keys1.every(key => deepEqual(norm1[key], norm2[key]));
};

/**
 * Compares two values with optional normalization
 * @param currentValues - Current form values
 * @param initialValues - Initial form values
 * @param normalizeValues - Optional function to normalize values before comparison
 * @returns true if values are equal (no changes detected)
 */
export const hasChanges = (
  currentValues: any,
  initialValues: any,
  normalizeValues?: (values: any) => any
): boolean => {
  if (currentValues === undefined || initialValues === undefined) {
    return true; // Can't compare, assume there are changes
  }

  let normalizedCurrent = currentValues;
  let normalizedInitial = initialValues;

  if (normalizeValues) {
    normalizedCurrent = normalizeValues({ ...currentValues });
    normalizedInitial = normalizeValues({ ...initialValues });
  }

  return !deepEqual(normalizedCurrent, normalizedInitial);
};


export const computeDiff = (
  currentValues: any,
  initialValues: any,
  normalizeValues?: (values: any) => any
): any => {
  if (!currentValues || !initialValues) {
    return currentValues || {};
  }

  // Normalize values if function provided
  let normalizedCurrent = currentValues;
  let normalizedInitial = initialValues;

  if (normalizeValues) {
    normalizedCurrent = normalizeValues({ ...currentValues });
    normalizedInitial = normalizeValues({ ...initialValues });
  }

  const diff: any = {};

  // Get all unique keys from both objects
  const allKeys = new Set([
    ...Object.keys(normalizedCurrent),
    ...Object.keys(normalizedInitial)
  ]);

  allKeys.forEach(key => {
    const currentVal = normalizedCurrent[key];
    const initialVal = normalizedInitial[key];

    // Check if values are different
    if (!deepEqual(currentVal, initialVal)) {
      // If both are objects (not arrays, not dates, not null), recursively compute diff
      if (
        typeof currentVal === 'object' &&
        typeof initialVal === 'object' &&
        currentVal !== null &&
        initialVal !== null &&
        !Array.isArray(currentVal) &&
        !Array.isArray(initialVal) &&
        !(currentVal instanceof Date) &&
        !(initialVal instanceof Date)
      ) {
        // Recursively compute diff for nested objects
        const nestedDiff = computeDiff(currentVal, initialVal);
        // Only include if there are actual changes in nested object
        if (Object.keys(nestedDiff).length > 0) {
          diff[key] = nestedDiff;
        }
      } else {
        // For primitives, arrays, dates, or when one is null/undefined
        // Include the current value (or undefined if it was removed)
        diff[key] = currentVal !== undefined ? currentVal : null;
      }
    }
  });

  return diff;
};

